import {useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import React from 'react';
import Card from '../card/Card';
import TitleCaption from '../card/TitleCaption';
import TitleNumber from '../card/TitleNumber';
import OldUrls from '../../../models/utils/OldUrls';
import Tooltip from '../../../ui/Tooltip';
import Money from '../../../utils/Money';

/**
 * Brand invoices dashboard card with statistics
 * @constructor
 */
export default function Invoices() {
  const {data, loading} = useQuery<InvoiceStatistics>(query);

  const max = data?.monthly.map((x) => x.amount).reduce((a, b) => Math.max(a, b), 0) || 0;

  return (
    <>
      <Card loading={loading} className="grid grid-cols-2">
        {data &&
          <>
            <InvoiceStats total={data.total.total} review={data.total.review} final={data.total.final}
                          paid={data.total.paid}/>
            <div className="flex justify-end">
              {data.monthly.map((x) =>
                <MonthColumn key={x.month} month={x.month} amount={Money.format(x.amount, x.currency)} value={x.amount}
                             max={max}/>)}
            </div>
          </>
        }
      </Card>
    </>);
}

type MonthColumnProps = {
  month: string,
  amount: string,
  value: number,
  max: number
};

function MonthColumn({month, amount, value, max}: MonthColumnProps) {
  // max height = 4 rem
  const height = max == 0 ? 0 : (value / max) * 4;
  return (
    <div className="w-7 h-full flex flex-col pt-1.5 mr-6">
      <span className="text-gray-dark uppercase text-sm">{month}</span>
      <Tooltip content={`Paid in ${month}: ${amount}`} label={month}>
        {height == 0 && <div className="bg-gray opacity-40  rounded-sm" style={{height: '4rem'}}/>}
        {height > 0 && <div className="bg-green  rounded-sm" style={{height: `${height}rem`}}/>}
      </Tooltip>
    </div>
  )
}

type TotalStatistics = {
  total: number
  review: number
  final: number
  paid: number
}

function InvoiceStats({total, review, final, paid}: TotalStatistics) {
  return (
    <div className="flex">
      <div className="flex-col px-5">
        <TitleCaption>Invoices</TitleCaption>
        <TitleNumber value={total}/>
      </div>
      <InvoiceStatNumber title="Review" type="review" value={review}/>
      <InvoiceStatNumber title="Final" type="final" value={final}/>
      <InvoiceStatNumber title="Paid" type="paid" value={paid}/>
    </div>
  )
}

type InvoiceStatNumberProps = {
  title: string
  type: string
  value: number
}

function InvoiceStatNumber({title, type, value}: InvoiceStatNumberProps) {
  return (
    <div className="flex flex-col pt-1.5 pr-6">
      <span>{title}</span>
      <a className="font-bold text-2xl underline" href={OldUrls.brandInvoices(type)}>{value}</a>
    </div>
  );
}

interface MonthlyStatistics {
  month: string,
  amount: number,
  currency: string,
}


interface InvoiceStatistics {
  total: TotalStatistics
  monthly: MonthlyStatistics[]
}

const query = gql`
  query InvoiceStatistics {
    total: invoiceStatistics {
      total
      review
      final
      paid
    }
    monthly: monthlyPaidStatistics {
      month
      amount
      currency
    }
  }
`;
